import {
  Directive, Input, Output, EventEmitter, HostBinding, HostListener,
  ContentChildren, QueryList, OnDestroy, Optional, Host, forwardRef,
} from '@angular/core';
import { Subscription } from 'rxjs';

let _optionId = 0;

/**
 * Primitivo HEADLESS de listbox — análogo ao `app-popover`, mas pro CONTEÚDO do menu.
 * Fornece o que era duplicado em group-menu e status-menu: roving (`activeIndex`),
 * teclado (↑/↓/Home/End/Enter/Espaço/Delete), `role`/`aria-activedescendant`/`aria-selected`
 * e os estados `.active`/`.selected`.
 *
 * É DIRETIVA (não componente com projeção) de propósito: o consumidor mantém o próprio DOM
 * e o próprio CSS dos itens (chips) — sem barreira de encapsulamento. A diretiva só adiciona
 * comportamento + as classes que o consumidor já estiliza.
 *
 * Uso:
 *   <div class="meu-menu" jemsListbox (select)="onSelect($event)" (deleteActive)="onDel($event)">
 *     <div class="minha-linha" *ngFor="let it of itens"
 *          [jemsListboxOption]="it" [jemsListboxSelected]="ehSelecionado(it)"> … </div>
 *   </div>
 */
// eslint-disable-next-line @angular-eslint/directive-selector -- primitivo headless: nome próprio `jemsListbox`, não componente de app
@Directive({ selector: '[jemsListbox]' })
export class JemsListboxDirective implements OnDestroy {
  @HostBinding('attr.role') readonly role = 'listbox';
  @HostBinding('attr.tabindex') readonly tabindex = '0';
  @HostBinding('attr.aria-activedescendant') get activeDescendant(): string | null {
    const opt = this.activeOption;
    return opt ? opt.id : null;
  }

  /** Item da opção ativada (Enter/Espaço/clique). */
  // eslint-disable-next-line @angular-eslint/no-output-native -- `select` é semântica de listbox (item ativado), não evento DOM nativo
  @Output() select = new EventEmitter<any>();
  /** Item da opção ativa ao pressionar Delete/Backspace (ex.: excluir grupo). */
  @Output() deleteActive = new EventEmitter<any>();

  @ContentChildren(forwardRef(() => JemsListboxOptionDirective))
  options: QueryList<JemsListboxOptionDirective>;

  // `activeIndex = -1` significa "sem opção ativa" (painel abre limpo, sem
  // pré-marcar o selecionado). Isso reflete a UX de app moderno: o cursor
  // do mouse ou o foco de teclado dita o realce — abertura fria não deve
  // sinalizar nada. Antes o diretivo pré-marcava a opção selecionada e o
  // mouseenter mudava — o item ficava "grudado" após o mouse sair do painel.
  activeIndex = -1;
  private sub?: Subscription;

  /** Opção ativa atual (lida pelos getters `.active` de cada opção — sem set imperativo). */
  get activeOption(): JemsListboxOptionDirective | null {
    if (this.activeIndex < 0) { return null; }
    return this.options ? (this.options.toArray()[this.activeIndex] || null) : null;
  }

  ngOnDestroy(): void { if (this.sub) { this.sub.unsubscribe(); } }

  /** Chamado pela opção no mouseenter (roving acompanha o ponteiro). Opção
   *  disabled não vira `.active` — sem highlight de algo que não pode ser
   *  escolhido. */
  setActive(opt: JemsListboxOptionDirective): void {
    if (opt.disabled) { return; }
    this.activeIndex = Math.max(0, this.options.toArray().indexOf(opt));
  }

  /** Mouse deixou o painel → limpa a opção ativa. Sem isso, o último item
   *  hovered mantinha `.active` mesmo com o cursor fora do painel. */
  @HostListener('mouseleave') onMouseLeaveHost(): void {
    this.activeIndex = -1;
  }

  /** Chamado pela opção no clique. */
  activateOption(opt: JemsListboxOptionDirective): void {
    this.select.emit(opt.item);
  }

  /** Próximo índice NÃO-disabled a partir de `from`, andando em `dir` (±1).
   *  Sem opção habilitada nessa direção, mantém `from` (nav não trava, só não anda). */
  private nextEnabledIndex(arr: JemsListboxOptionDirective[], from: number, dir: 1 | -1): number {
    for (let i = from + dir; i >= 0 && i <= arr.length - 1; i += dir) {
      if (!arr[i].disabled) { return i; }
    }
    return from;
  }

  @HostListener('keydown', ['$event'])
  onKeydown(ev: KeyboardEvent): void {
    const arr = this.options.toArray();
    const last = arr.length - 1;
    // 1ª tecla depois de painel frio (activeIndex == -1) → seed no selecionado
    // ou no primeiro HABILITADO. As setas então movem a partir daí.
    const cold = this.activeIndex < 0;
    const seed = () => {
      const sel = arr.findIndex(o => o.selected && !o.disabled);
      this.activeIndex = sel >= 0 ? sel : arr.findIndex(o => !o.disabled);
    };
    switch (ev.key) {
      case 'ArrowDown':
        ev.preventDefault();
        if (cold) { seed(); } else { this.activeIndex = this.nextEnabledIndex(arr, this.activeIndex, 1); }
        break;
      case 'ArrowUp':
        ev.preventDefault();
        if (cold) { seed(); } else { this.activeIndex = this.nextEnabledIndex(arr, this.activeIndex, -1); }
        break;
      case 'Home': ev.preventDefault(); this.activeIndex = arr.findIndex(o => !o.disabled); break;
      case 'End': {
        ev.preventDefault();
        let lastEnabled = -1;
        for (let i = last; i >= 0; i--) { if (!arr[i].disabled) { lastEnabled = i; break; } }
        this.activeIndex = lastEnabled;
        break;
      }
      case 'Enter':
      case ' ':
        ev.preventDefault();
        if (arr[this.activeIndex] && !arr[this.activeIndex].disabled) { this.select.emit(arr[this.activeIndex].item); }
        break;
      case 'Delete':
      case 'Backspace':
        if (arr[this.activeIndex]) {
          ev.preventDefault();
          this.deleteActive.emit(arr[this.activeIndex].item);
        }
        break;
    }
  }
}

// eslint-disable-next-line @angular-eslint/directive-selector -- primitivo headless: nome próprio `jemsListboxOption`
@Directive({ selector: '[jemsListboxOption]' })
export class JemsListboxOptionDirective {
  /** Item que esta opção representa (devolvido em `select`/`deleteActive`). */
  @Input('jemsListboxOption') item: any;
  /** Marca a opção como SELECIONADA (faixa persistente + aria-selected). */
  // eslint-disable-next-line @angular-eslint/no-input-rename -- microsyntax do diretivo: input casa o seletor [jemsListboxSelected]
  @Input('jemsListboxSelected') @HostBinding('class.selected') selected = false;
  /** Opção não-selecionável (ex.: track fechada). Bloqueia clique, hover-roving
   *  e navegação por teclado; consumidor decide o estilo via `.disabled`. */
  // eslint-disable-next-line @angular-eslint/no-input-rename -- microsyntax do diretivo: input casa o seletor [jemsListboxOptionDisabled]
  @Input('jemsListboxOptionDisabled') @HostBinding('class.disabled') disabled = false;

  @HostBinding('attr.role') readonly role = 'option';
  @HostBinding('attr.id') readonly id = `jems-opt-${_optionId++}`;
  @HostBinding('attr.aria-selected') get ariaSelected(): string { return String(!!this.selected); }
  @HostBinding('attr.aria-disabled') get ariaDisabled(): string { return String(!!this.disabled); }
  /** `.active` por getter (avaliado no CD) — sem set imperativo, evita ExpressionChanged. */
  @HostBinding('class.active') get active(): boolean {
    return !!this.listbox && this.listbox.activeOption === this;
  }

  constructor(@Optional() @Host() private listbox: JemsListboxDirective) {}

  @HostListener('mouseenter') onMouseEnter(): void { if (this.listbox) { this.listbox.setActive(this); } }
  @HostListener('click') onClick(): void { if (this.listbox && !this.disabled) { this.listbox.activateOption(this); } }
}
